import { Routes, Route, BrowserRouter } from 'react-router-dom';
import {
 QueryClient,
 QueryClientProvider,
} from '@tanstack/react-query';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import AnalyticsProvider from './components/AnalyticsProvider/AnalyticsProvider';

import './styles/global.scss';
import './styles/skeleton.scss';

import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { PromptProvider } from './context/PromptContext';
import { NotificationProvider } from './context/NotificationContext';

import Homepage from './pages/Home';
import Login from './pages/Login';
import LoginArea from './components/Login/LoginSection';
import ForgotPassword from './components/ForgotPassword/ForgotPasswordSection';
import Signup from './pages/Signup';
import SignUpArea from './components/Signup/SignupSection';
import ProductListing from './pages/ProductListing';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import ToastNotification from './components/ToastNotification/Notification';
import AppPrompt from './components/AppPrompt/AppPrompt';
import Account from './pages/Account';
import AccountCart from './components/AccountCart/AccountCart';
import Orders from './components/Orders/OrdersSection';
import Referrals from './components/Referrals/ReferralsSection';
import Wishlist from './components/Wishlist/WishlistSection';
import Address from './components/Address/Address';
import Track from './components/TrackOrder/TrackSection';
import Tracking from './pages/Tracking';
import Order from './pages/Order';
import ReturnedOrder from './pages/ReturnedOrder';
import TrackingDetails from './pages/TrackingDetails';
import CartSide from './components/CartSide/CartSide';
import AddBilling from './components/AddBilling/AddBilling';
import Checkout from './pages/Checkout';
import AuthRequired from './pages/AuthRequired';
import AuthRoute from './protectedRoutes/AuthRoute';
import Settings from './components/Settings/SettingsSection';
import Profile from './components/Profile/ProfileSection';
import Cards from './components/Cards/CardsSection';
import { LoadScript } from '@react-google-maps/api';
import { UserProvider } from './context/UserContext';
import ViewAll from './pages/ViewAll';
import VerifyOtpCustomer from './components/VerifyOTPCustomer/VerifyOtpCustomer';
import VerifyOTP from './components/VerifyOTP/VerifyOTPSection';
import ResetPasswordSection from './components/ResetPasswordComponent/ResetPasswordSection';
import Coupons from './components/Coupons/CouponsSection';
import ReferralPage from './pages/Referral';
import PaymentSuccessful from './pages/PaymentSuccessful';
import PrivacyPolicy from './pages/privacypolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Contact from './pages/Contact';
import AboutUs from './pages/AboutUs';

const queryClient = new QueryClient();

function App() {
 return (
  <ErrorBoundary>
   <QueryClientProvider client={queryClient}>
    <LoadScript
     googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API!}
     libraries={['places']}
    >
     <BrowserRouter>
      <AuthProvider>
       <AnalyticsProvider>
        <PromptProvider>
         <NotificationProvider>
          <UserProvider>
           <CartProvider>
            <Routes>
             <Route
              index
              element={<Homepage />}
             />

             <Route
              path="home"
              element={<Homepage />}
             />
             <Route
              path="products/:category?/:subcategory?"
              element={<ProductListing />}
             />
             <Route
              path="product/:productid?/:isLiked?"
              element={<ProductDetail />}
             />
             <Route
              path="track"
              element={<Tracking />}
             />
             <Route
              path="viewall/:type?"
              element={<ViewAll />}
             />
             <Route
              path="referral/:referral_id?"
              element={<ReferralPage />}
             />
             <Route
              path="checkout/:coupon_id?"
              element={
               <AuthRoute>
                <Checkout />
               </AuthRoute>
              }
             />
             <Route
              path="order/:orderid"
              element={
               <AuthRoute>
                <Order />
               </AuthRoute>
              }
             />
             <Route
              path="returned-order/:id"
              element={
               <AuthRoute>
                <ReturnedOrder />
               </AuthRoute>
              }
             />
             <Route
              path="tracking/:orderid/:trackingid?"
              element={
               <AuthRoute>
                <TrackingDetails />
               </AuthRoute>
              }
             />

             <Route
              path="authprompt"
              element={<AuthRequired />}
             />
             <Route
              path="payments/successful"
              element={<PaymentSuccessful />}
             />
             <Route
              path="privacypolicy"
              element={<PrivacyPolicy />}
             />
             <Route
              path="terms-conditions"
              element={<TermsAndConditions />}
             />
             <Route
              path="contact"
              element={<Contact />}
             />
             <Route
              path="aboutUs"
              element={<AboutUs />}
             />

             <Route
              path="account"
              element={
               <AuthRoute>
                <Account />
               </AuthRoute>
              }
             >
              <Route
               index
               element={<Profile />}
              />
              <Route
               path="profile"
               element={<Profile />}
              />
              <Route
               path="settings"
               element={<Settings />}
              />
              <Route
               path="cart"
               element={<AccountCart />}
              />
              <Route
               path="orders"
               element={<Orders />}
              />
              <Route
               path="referrals"
               element={<Referrals />}
              />
              <Route
               path="wishlist"
               element={<Wishlist />}
              />
              <Route
               path="address"
               element={<Address />}
              />
              <Route
               path="track"
               element={<Track />}
              />
              <Route
               path="cards"
               element={<Cards />}
              />
              <Route
               path="coupons"
               element={<Coupons />}
              />
             </Route>

             <Route
              path="cart"
              element={<Cart />}
             >
              <Route
               index
               element={<CartSide />}
              />
              <Route
               path="addbilling"
               element={<AddBilling />}
              />
             </Route>

             {/* Sign In And Sub-Routes */}
             <Route
              path="signin"
              element={<Login />}
             >
              <Route
               index
               element={<LoginArea />}
              />
              <Route
               path="forgotpassword"
               element={<ForgotPassword />}
              />
              <Route
               path="resetpassword"
               element={<ResetPasswordSection />}
              />
              <Route
               path="verify"
               element={<VerifyOTP />}
              />
             </Route>

             {/* Sign Up And Sub-routes */}
             <Route
              path="signup/:referral_id?"
              element={<Signup />}
             >
              <Route
               index
               element={<SignUpArea />}
              />
              <Route
               path="verify"
               element={<VerifyOtpCustomer />}
              />
             </Route>
            </Routes>

            <ToastNotification></ToastNotification>
            <AppPrompt></AppPrompt>
           </CartProvider>
          </UserProvider>
         </NotificationProvider>
        </PromptProvider>
       </AnalyticsProvider>
      </AuthProvider>
     </BrowserRouter>
    </LoadScript>
   </QueryClientProvider>
  </ErrorBoundary>
 );
}

export default App;
